import * as React from "react";
import classNames from "classnames";

import {
  enforceSequentialResponse,
  OutOfOrderResponseError,
} from "lib/utils/enforceSequentialResponse";
import { debounce, pluralise } from "./landing_utils";

import css from "./SearchDropdown.module.scss";

import MAGNIFYING_GLASS from "../../../assets/images/icons/magnifying_glass.svg";

export type SearchCategory = "buyer" | "topic" | "source";

type Link = { name: string; href: string; notice_count: number };

const performSearch = enforceSequentialResponse(async (category: SearchCategory, value: string) =>
  fetch(`/explore/api/landing_pages/search?category=${category}&text=${value}`),
);

export const handlePerformSearch = async (
  cat: SearchCategory,
  value: string,
): Promise<{ results: Link[] }> => {
  let resp: Response | undefined = undefined;
  try {
    resp = await performSearch(cat, value);
  } catch (e) {
    if (e instanceof OutOfOrderResponseError) {
      // no-op - it's ok
    } else {
      // TODO: notify us of e?
      console.error(e);
    }
  }
  if (resp?.status === 200) {
    return resp.json();
  } else {
    // TODO: notify us??
    console.error(resp ? resp.text() : "No response received");
  }

  return { results: [] };
};

const debouncedSearchPages = debounce<typeof handlePerformSearch>(handlePerformSearch, 300);

function SearchDropdown({ category, className }: { category: SearchCategory; className?: string }) {
  const [value, setValue] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [suggestions, setSuggestions] = React.useState<Link[]>([]);
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  const focusInput = React.useCallback(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  // Reset the suggestions and keep input focused when switching mode
  React.useEffect(() => {
    setSuggestions([]);
    focusInput();
  }, [category, focusInput]);

  React.useEffect(() => {
    void (async () => {
      // Searching by just 1 char or empty string is neither particularly helpful for a user, nor
      // performant (at the moment, at least)
      if (value.length > 1) {
        setLoading(true);
        const resp = await debouncedSearchPages(category, value);

        setSuggestions(resp.results);
        setLoading(false);
      }
    })();
  }, [value, category]);

  return (
    <div className={classNames(css.searchDropdown, className)}>
      <div className={css.inputContainer} onClick={focusInput}>
        <img src={MAGNIFYING_GLASS} height="32" width="32" alt="Magnifying glass" />
        <input
          value={value}
          type="text"
          onChange={(e) => setValue(e.currentTarget.value)}
          ref={inputRef}
          placeholder={`Search by ${category}...`}
        />
      </div>
      {value && (
        <ul className={css.suggestions}>
          {loading ? (
            <div>Loading...</div>
          ) : suggestions.length ? (
            suggestions.map((s) => (
              <li key={s.href}>
                <a href={s.href}>
                  <span>{s.name}</span>
                  <span>{pluralise(s.notice_count, "notice", true)}</span>
                </a>
              </li>
            ))
          ) : (
            <div>No results found. Try broadening your search</div>
          )}
        </ul>
      )}
    </div>
  );
}

export default SearchDropdown;
